const NEEDS_QUOTING = /[",\r\n]/;

// Leading characters a spreadsheet evaluates as a formula rather than text.
const FORMULA_LEAD = /^[=+\-@]/;

// Whitespace and control characters a spreadsheet discards on import, so they cannot be relied on
// to keep a value inert.
const IMPORT_TRIMMED = /^[\s\u0000-\u001F]+/;

/**
 * Stops an exported cell being executed as a formula by Excel or Sheets.
 *
 * Names in these exports come from customer-supplied data, so a company called
 * `=HYPERLINK("http://evil","click")` becomes a live formula on the reviewer's machine when the CSV
 * is opened. Prefixing with an apostrophe forces the cell to text; the apostrophe is not shown.
 *
 * Tested against the value with leading whitespace and control characters removed, because Excel and
 * Sheets trim on import: ` =HYPERLINK(...)` and `\n=HYPERLINK(...)` both execute despite not
 * starting with `=` in the file.
 *
 * Only a leading `-` is exempted, and only when the whole value is a number: a negative number is
 * not a formula and quoting it would break every numeric column downstream. `+1` is NOT exempt -
 * Excel evaluates it as the formula `=1`.
 */
const neutralizeFormula = stringValue => {
  const asImported = stringValue.replace(IMPORT_TRIMMED, '');
  if (!FORMULA_LEAD.test(asImported)) return stringValue;
  if (asImported.startsWith('-') && Number.isFinite(Number(asImported))) return stringValue;
  return "'".concat(stringValue);
};

// RFC 4180: quote the value if it contains a comma, quote, CR or LF; double embedded quotes.
export const escapeCSVValue = value => {
  if (value === null || value === undefined) {
    return '';
  }
  const stringValue = neutralizeFormula(String(value));
  return NEEDS_QUOTING.test(stringValue) ? "\"".concat(stringValue.replace(/"/g, '""'), "\"") : stringValue;
};
export const buildCSVContent = (headerCells, dataRows) => [headerCells, ...dataRows].map(row => row.map(escapeCSVValue).join(',')).join('\n');
export const exportCSV = (headerCells, dataRows, name) => {
  const csvContent = buildCSVContent(headerCells, dataRows);
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Each object URL pins its blob until the document unloads. These pages export repeatedly.
  URL.revokeObjectURL(url);
};
const getExportValue = (col, row) => {
  if (!col.renderCell) {
    return row[col.field] !== undefined ? row[col.field] : 'N/A';
  }
  // Mimic renderCell output: prefer the export-specific getter, then the display getter.
  if (col.valueGetterForExport) {
    return col.valueGetterForExport({
      row
    });
  }
  if (col.valueGetter) {
    return col.valueGetter({
      row
    });
  }
  return row[col.field] !== undefined ? row[col.field] : 'N/A';
};
export const exportCSVFromDataGrid = _ref => {
  let columns = _ref.columns,
    name = _ref.name,
    rows = _ref.rows;
  const visibleColumns = columns.filter(col => col.isExportable);
  const headerCells = visibleColumns.map(col => typeof col.headerName === 'string' ? col.headerName.replace(/\n/g, ' ') : col.headerName);
  const dataRows = rows.map(row => visibleColumns.map(col => getExportValue(col, row)));
  exportCSV(headerCells, dataRows, name);
};